import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    datetime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToReservation = () => {
    const element = document.getElementById('reservation');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('datetime', formData.datetime);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4v0t6nsdr6u9jrifkgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', datetime: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white font-['Noto_Sans_JP']">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <a href="#company" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>会社情報</a>
            <a href="#access" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>アクセス</a>
          </div>
          <button onClick={scrollToReservation} className="bg-white text-gray-900 px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
            見学予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/c8e8c8bb22b779c3ed169fbbb29437d4.jpeg" 
            alt="SmALL展示会" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl">
            運べる家<br />ミニラボ
          </h1>
          <p className="text-xl md:text-2xl text-white mb-10 max-w-3xl font-light">
            お金・場所・将来に縛られず、人生を自由に設計できる住まい
          </p>
          <button onClick={scrollToReservation} className="bg-white text-gray-900 px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer">
            今すぐ見学予約
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-lg font-semibold text-gray-600 mb-4 uppercase tracking-wider">こんな不安、ありませんか？</div>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight">
                長期ローン、<br />場所の固定、<br />将来への不安
              </h2>
            </div>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">長期ローンに縛られることに不安</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">家を買うことで住む場所に縛られる</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-gray-800 mt-2 flex-shrink-0"></div>
                <p className="text-xl text-gray-700">今、本当に持ち家が必要なのかわからない</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">ミニラボ展示会情報</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer">
              <div className="w-full" style={{ aspectRatio: '4/3' }}>
                <img 
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/44779bf68fdfb854e387080ce0157183.jpeg" 
                  alt="外観デザイン" 
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold">外観デザイン</h3>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8 flex flex-col justify-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">ミニラボ展示会</h3>
                  <p className="text-gray-600">移動できる住まいを実際に体験</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">開催日</div>
                      <div className="text-lg text-gray-900">毎日開催（水曜定休）</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">開催時間</div>
                      <div className="text-lg text-gray-900">10:00〜17:00</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">住所</div>
                      <div className="text-lg text-gray-900">秋田県湯沢市田町2-1-3</div>
                    </div>
                  </div>
                </div>
                <button onClick={scrollToReservation} className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">
                  見学予約する
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-6 py-3 rounded-full text-lg font-semibold mb-4">
              メリット
            </div>
            <h2 className="text-4xl font-bold text-gray-900">3つの特徴</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">住宅ローンの早期完了</h3>
              <p className="text-gray-600 text-center leading-relaxed">15年～20年の短い期間で住宅ローン返済ができるから、とても経済的です</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-truck-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">運搬可能な「動産」</h3>
              <p className="text-gray-600 text-center leading-relaxed">引越しの際はトラック運搬で建物ごと移動できます</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-group-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">1人・2人暮らしに適した家</h3>
              <p className="text-gray-600 text-center leading-relaxed">少人数暮らしの建物です。店舗や事務所としても活用できます</p>
            </div>
          </div>
        </div>
      </section>

      {/* Plans & Pricing Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-5 py-2 rounded-full text-lg font-semibold mb-4">
              プラン紹介
            </div>
            <h2 className="text-4xl font-bold text-gray-900">商品プラン・価格</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            {/* 住居プラン */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="bg-teal-600 text-white py-6 px-8">
                <h3 className="text-3xl font-bold text-center">住居プラン</h3>
              </div>
              <div className="p-8">
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">外観デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full" style={{ aspectRatio: '4/3' }}>
                    <img 
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/4d8fb089c6ba4332ce03bc206d502495.jpeg" 
                      alt="住居プラン外観" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">内装デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full" style={{ aspectRatio: '4/3' }}>
                    <img 
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/56c66290d59ae98c1f7d29e4b89fc0b7.jpeg" 
                      alt="住居プラン内装" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="bg-teal-50 rounded-2xl p-6 text-center mb-6">
                  <div className="text-sm font-semibold text-teal-600 mb-2">コミコミ価格</div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">1,098<br /><span className="text-2xl">万円〜</span></div>
                  <div className="text-sm text-gray-600">（税込・基礎工事・付帯工事・エアコン含む）</div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-4 text-center">お支払い例</div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">頭金なし・金利0.95％・借入額1098万円の場合</span>
                      <span className="text-lg font-bold text-gray-900"></span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">20年ローン</span>
                      <span className="text-lg font-bold text-gray-900">月々 50,251円</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">※お支払い例は条件により変動します。</p>
                </div>
                <button onClick={scrollToReservation} className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">
                  このプランで見学予約
                </button>
              </div>
            </div>

            {/* 店舗プラン */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="bg-gray-900 text-white py-6 px-8">
                <h3 className="text-3xl font-bold text-center">店舗プラン</h3>
              </div>
              <div className="p-8">
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">外観デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full" style={{ aspectRatio: '4/3' }}>
                    <img 
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/779a25bce501d00ce6ec29d8a1ec512d.jpeg" 
                      alt="店舗プラン外観" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">内装デザイン</h4>
                  <div className="rounded-2xl overflow-hidden shadow-lg w-full" style={{ aspectRatio: '4/3' }}>
                    <img 
                      src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/f5cf191cedff64c640f40433442c67ea.jpeg" 
                      alt="店舗プラン内装" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="bg-gray-100 rounded-2xl p-6 text-center mb-6">
                  <div className="text-sm font-semibold text-gray-600 mb-2">コミコミ価格</div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">1,045<br /><span className="text-2xl">万円〜</span></div>
                  <div className="text-sm text-gray-600">（税込・基礎工事・付帯工事・エアコン含む）</div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-4 text-center">お支払い例</div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">頭金なし・金利0.95％・借入額1045万円の場合</span>
                      <span className="text-lg font-bold text-gray-900"></span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">20年ローン</span>
                      <span className="text-lg font-bold text-gray-900">月々 47,826円</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">※お支払い例は条件により変動します。</p>
                </div>
                <button onClick={scrollToReservation} className="w-full bg-gray-900 text-white py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
                  このプランで見学予約
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16 items-center">
            <div className="md:col-span-2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                小ささを楽しめば、<br />あとはメリットしかない
              </h2>
            </div>
            <div className="md:col-span-3 grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">小ささは高性能。エコで快適な住み心地</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">20年返済の住宅ローンで月々無理をしない</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">必要に応じて移設が可能</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-teal-600 mt-1 flex-shrink-0"></i>
                <p className="text-gray-700 leading-relaxed">店舗・事務所など、幅広い活用が可能</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">こんな方におすすめ</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hand-coin-line text-4xl text-white"></i>
              </div>
              <p className="text-gray-700 leading-relaxed">長期の住宅ローンに縛られない自由な暮らしをしたい方</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-briefcase-line text-4xl text-white"></i>
              </div>
              <p className="text-gray-700 leading-relaxed">家を持つことで、将来の選択肢（転職・働き方）を狭めたくない方</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-group-line text-4xl text-white"></i>
              </div>
              <p className="text-gray-700 leading-relaxed">家族構成やライフスタイルの変化に合わせて、住まいも柔軟に変えたい方</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-5 py-2 rounded-full text-lg font-semibold mb-4">
              お客様の声
            </div>
            <h2 className="text-4xl font-bold text-gray-900">実際に体験された方の声</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">42歳</div>
                  <div className="text-sm text-gray-600">1人暮らし・会社員</div>
                </div>
              </div>
              <i className="ri-double-quotes-l text-4xl text-teal-200 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed">一人で戸建ては贅沢かなと思ってましたが、この絶妙なサイズ感を見た瞬間に『これだ！』ってピンときました。管理費もかからないし、自分だけの城を手に入れた満足感でいっぱいです。毎日帰るのが楽しみになりました！</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">35歳</div>
                  <div className="text-sm text-gray-600">フリーランス</div>
                </div>
              </div>
              <i className="ri-double-quotes-l text-4xl text-teal-200 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed">自宅兼アトリエにしたかったので、普通の家じゃなく『店舗っぽさ』がある建物を探していました。このカッコよさは一目惚れですね。お客さんにも『おしゃれ！』と褒められるし、仕事のモチベーションも爆上がりです！</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">51歳</div>
                  <div className="text-sm text-gray-600">2人暮らし・会社員</div>
                </div>
              </div>
              <i className="ri-double-quotes-l text-4xl text-teal-200 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed">この年齢での家づくりはローンの長さが一番不安でした。でも、ミニラボなら15年で完済できると聞いてパッと視界が開けた感じで希望が持てました。定年までに払い終えられる安心感のおかげで、夫婦二人、穏やかな老後を迎えられそうです。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Q&A Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white px-5 py-2 rounded-full text-lg font-semibold mb-4">
              よくある質問
            </div>
            <h2 className="text-4xl font-bold text-gray-900">Q&A</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">工事にどれくらい時間がかかりますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">工場で木造ユニットを組み立てて現地で設置するため、２カ月前後でお引渡しができます。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">建物価格はどれくらいですか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">建物は、税込1045万円からご提案しています。この価格には基礎工事など必要な工事費も含まれています。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">住宅ローンは使えますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">はい、各住宅ローンの利用ができます。資金面の不安があれば専門スタッフが相談に応じます。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">設置できる場所に制限はありますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">トラック輸送するため道路幅やクレーン作業での電線などの検討が必要です。専門スタッフが現地調査を行い、設置可能かどうか確認します。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q5</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">断熱材はどうなっていますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">断熱材は、2030年対応の等級5（ZEH）が標準仕様。夏は涼しく冬は暖かい室内空間になっております。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q6</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">施工後の保証やアフターサービスはありますか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">住まいの品質と安心を保つために、お引渡し後は、構造体１０年保証や水回り設備保証などのサポート体制も整えています。</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q7</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 pt-2">秋田県内での施工エリアはどこまでですか？</h3>
              </div>
              <div className="flex items-start gap-4 pl-16">
                <p className="text-gray-700 leading-relaxed">設置可能な地域については、スタッフがご相談の上、対応可能エリアを確認します。遠方の場合もご相談ください。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Section */}
      <section className="py-24 bg-amber-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block bg-white/80 backdrop-blur-sm text-amber-900 px-5 py-2 rounded-full text-sm font-semibold mb-6">
            特典
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-10 leading-tight">
            今なら来場予約で、<br />クオカード500円プレゼント
          </h2>
          <button onClick={scrollToReservation} className="bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-all flex items-center gap-3 mx-auto whitespace-nowrap cursor-pointer">
            見学予約はこちら
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section id="reservation" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16">
            <div className="md:col-span-2">
              <div className="text-sm font-semibold text-gray-600 mb-4 uppercase tracking-wider">見学予約</div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                展示会を<br />体験してください
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                実際のミニラボをご覧ください。専門スタッフが丁寧にご案内いたします。
              </p>
              <p className="text-gray-600 leading-relaxed">
                ご予約いただいた方には、もれなくクオカード500円をプレゼントいたします。
              </p>
            </div>
            <div className="md:col-span-3">
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-xl" data-readdy-form id="reservation-form">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">お名前 <span className="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">電話番号 <span className="text-red-500">*</span></label>
                    <input 
                      type="tel" 
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">メールアドレス <span className="text-red-500">*</span></label>
                    <input 
                      type="email" 
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      placeholder="example@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="datetime" className="block text-sm font-semibold text-gray-700 mb-2">見学予約日時 <span className="text-red-500">*</span></label>
                    <input 
                      type="datetime-local" 
                      id="datetime"
                      name="datetime"
                      value={formData.datetime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">ご質問、ご要望</label>
                    <textarea 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm"
                      placeholder="ご質問やご要望がございましたらご記入ください（500文字以内）"
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</div>
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '送信中...' : '予約を確定する'}
                  </button>
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                      ご予約ありがとうございます。確認メールをお送りいたしました。
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                      送信に失敗しました。もう一度お試しください。
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info & Map Section */}
      <section id="company" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">運営会社情報</h2>
              <div className="space-y-6">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">運営会社</div>
                  <div className="text-lg text-gray-900">湯沢開発株式会社</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">代表者</div>
                  <div className="text-lg text-gray-900">高橋正明</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">所在地</div>
                  <div className="text-lg text-gray-900">秋田県湯沢市田町2-1-3</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">電話番号</div>
                  <div className="text-lg text-gray-900">0183-73-7125</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">定休日</div>
                  <div className="text-lg text-gray-900">水曜日</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">創業</div>
                  <div className="text-lg text-gray-900">1951年</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">宅地建物取引業許可</div>
                  <div className="text-lg text-gray-900">秋田県知事免許(17)第43号</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">建設業登録</div>
                  <div className="text-lg text-gray-900">秋田県知事許可（般-4）第80084号</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">一級建築士事務所</div>
                  <div className="text-lg text-gray-900">秋田県知事登録 第16-110A-0250号</div>
                </div>
              </div>
            </div>
            <div id="access">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">展示会アクセス</h2>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d1546.8092463002413!2d140.49084285597093!3d39.160658072616755!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMznCsDA5JzM4LjQiTiAxNDDCsDI5JzMxLjciRQ!5e0!3m2!1sja!2sjp!4v1765608274945!5m2!1sja!2sjp" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="展示会アクセスマップ"
                ></iframe>
              </div>
              <p className="text-sm text-gray-600 mt-4">秋田県湯沢市田町2-1-3</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl font-bold mb-6">運べる家ミニラボ</h3>
              <p className="text-teal-100 leading-relaxed mb-6">
                移動できる住まいで、小ささを楽しめば、あとはメリットしかない。長期ローンや場所に縛られない、新しい暮らし方を提案します。
              </p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-teal-900 transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://www.y-kaihatsu.co.jp/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-teal-900 transition-all cursor-pointer">
                  <i className="ri-line-fill"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">お問い合わせ</h4>
              <div className="space-y-3 text-teal-100">
                <p>秋田県湯沢市田町2-1-3</p>
                <p>TEL: 0183-73-7125</p>
                <p>定休日: 水曜日</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">営業時間</h4>
              <div className="space-y-2 text-teal-100">
                <p>水曜日：定休日</p>
              </div>
            </div>
          </div>
          <div className="border-t border-teal-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-teal-200">Copyright &copy; 2025 湯沢開発株式会社</p>
            <div className="flex gap-6 text-sm">
              <a href="https://www.y-kaihatsu.co.jp/" target="_blank" rel="noopener noreferrer" className="text-teal-200 hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
